import { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { ResponsivePopover as Popover, ResponsivePopoverContent as PopoverContent, ResponsivePopoverTrigger as PopoverTrigger } from "./ui/responsive-popover";
import { ChevronDown, Loader2, X } from "lucide-react";

// Choix d'une personne de l'annuaire (responsable, chef de service…). La recherche se fait côté serveur et ne propose que ce que
// l'appelant a le droit de voir ; les personnes sorties ne sont pas proposées (le serveur les refuserait de toute façon).
export default function CollaboratorPicker({ value, label, onChange, disabled, excludeId, testid, placeholder = "Aucun", includeArchived = false, includeExited = false, allowAccounts = false }) {
  const searchRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        let list;
        if (includeArchived || includeExited) {
          // callers that must see archived/exited people (read-only pickers) use the full directory search
          const { data } = await api.get("/collaborators", { params: { search: query || undefined, limit: 20, include_archived: includeArchived } });
          list = data.items.filter((i) => includeExited || i.status !== "exited").map((i) => ({ ...i, kind: "collaborator" }));
        } else {
          const { data } = await api.get("/collaborators/manager-candidates", { params: { search: query || undefined, limit: 20, include_accounts: allowAccounts || undefined } });
          list = data.items;
        }
        if (!cancelled) setItems(list.filter((i) => i.kind !== "collaborator" || i.id !== excludeId));
      } catch (e) {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, query, excludeId, includeArchived, includeExited, allowAccounts]);

  const pick = (id, name, kind) => {
    onChange(id, name, kind);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" disabled={disabled} data-testid={testid} className="w-full justify-between font-normal h-10">
          <span className={`truncate ${value ? "text-slate-900" : "text-slate-500"}`}>{value ? label || "Personne sélectionnée" : placeholder}</span>
          <ChevronDown size={14} className="text-slate-400 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start" className="w-[min(20rem,calc(100vw-2rem))] p-2 space-y-2"
        onOpenAutoFocus={(e) => { e.preventDefault(); searchRef.current?.focus({ preventScroll: true }); }}
      >
        <Input ref={searchRef} placeholder="Rechercher par nom ou référence" value={query} onChange={(e) => setQuery(e.target.value)} data-testid={`${testid}-search`} />
        <div className="max-h-56 overflow-y-auto" role="listbox">
          {loading && <div className="flex justify-center py-3"><Loader2 className="animate-spin text-slate-400" size={16} /></div>}
          {!loading && items.length === 0 && <div className="py-3 text-center text-xs text-slate-500">Aucune personne trouvée</div>}
          {!loading && items.map((item) => (
            <button
              key={`${item.kind}-${item.id}`} type="button" role="option" aria-selected={item.id === value}
              onClick={() => pick(item.id, item.display_name, item.kind)} data-testid={`${testid}-option-${item.id}`}
              className="w-full text-left px-2 py-2 rounded-md text-sm hover:bg-slate-100 min-h-[44px] md:min-h-0"
            >
              <div className="text-slate-900">{item.display_name}</div>
              <div className="text-xs text-slate-500">{item.kind === "account" ? "Compte CRM, sans fiche" : item.reference}</div>
            </button>
          ))}
        </div>
        {value && (
          <Button type="button" variant="ghost" size="sm" className="w-full text-slate-600" onClick={() => pick(null, null)} data-testid={`${testid}-clear`}>
            <X size={14} className="mr-1.5" /> Retirer
          </Button>
        )}
      </PopoverContent>
    </Popover>
  );
}
